import React from 'react';
import { motion } from 'framer-motion';
import { FiBriefcase, FiUserPlus, FiClock, FiCheckCircle } from 'react-icons/fi';
import SafeIcon from '../components/SafeIcon';
import { mockRoles, upcomingInterviews, mockCandidates } from '../data/mockData';

const StatCard = ({ icon, label, value, trend, trendUp }) => (
  <motion.div 
    whileHover={{ y: -2 }}
    className="bg-slate-900 border border-slate-800 p-5 rounded-xl relative overflow-hidden"
  >
    <div className="absolute -right-6 -top-6 w-24 h-24 bg-blue-500/5 rounded-full blur-xl"></div>
    <div className="flex justify-between items-start mb-4">
      <div className="p-2 bg-slate-800 rounded-lg text-blue-400">
        <SafeIcon icon={icon} className="text-xl" />
      </div>
      <span className={`text-xs font-medium px-2 py-1 rounded-full ${trendUp ? 'bg-green-500/10 text-green-400' : 'bg-rose-500/10 text-rose-400'}`}>
        {trend}
      </span>
    </div>
    <h3 className="text-slate-400 text-sm font-medium">{label}</h3>
    <p className="text-2xl font-bold text-slate-100 mt-1">{value}</p>
  </motion.div>
);

const DashboardView = () => {
  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-100 flex items-center gap-2">
          <span className="w-2 h-8 bg-blue-500 rounded-full shadow-[0_0_10px_rgba(59,130,246,0.5)]"></span>
          Command Center
        </h1>
        <button className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-[0_0_15px_rgba(59,130,246,0.3)]">
          + New Requisition
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={FiBriefcase} label="Active Roles" value="12" trend="+2 this week" trendUp={true} />
        <StatCard icon={FiUserPlus} label="Total Pipeline" value="342" trend="+18% vs last mo" trendUp={true} />
        <StatCard icon={FiClock} label="Avg Time to Hire" value="28 days" trend="-3 days" trendUp={true} />
        <StatCard icon={FiCheckCircle} label="Offers Accepted" value="8" trend="92% acceptance" trendUp={true} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-slate-900 border border-slate-800 rounded-xl p-5">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-slate-200">Active Requisitions</h2>
            <button className="text-blue-400 text-sm hover:text-blue-300">View All</button>
          </div>
          <div className="space-y-3">
            {mockRoles.map(role => (
              <div key={role.id} className="flex items-center justify-between p-3 hover:bg-slate-800/50 rounded-lg border border-transparent hover:border-slate-700 transition-colors cursor-pointer">
                <div>
                  <h3 className="text-slate-200 font-medium">{role.title}</h3>
                  <p className="text-xs text-slate-500">{role.department} • Open {role.daysOpen} days</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-sm text-slate-300">{role.applicants} candidates</p>
                    <p className="text-xs text-slate-500">Target: {role.target}</p>
                  </div>
                  <div className={`px-2 py-1 rounded text-xs font-medium border ${
                    role.status === 'Urgent' ? 'bg-rose-500/10 text-rose-400 border-rose-500/20' : 
                    role.status === 'Active' ? 'bg-green-500/10 text-green-400 border-green-500/20' : 
                    'bg-slate-800 text-slate-400 border-slate-700'
                  }`}>
                    {role.status}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 flex flex-col">
          <h2 className="text-lg font-semibold text-slate-200 mb-4">Action Radar</h2>
          <div className="flex-1 space-y-4">
            {upcomingInterviews.map(interview => {
              const candidate = mockCandidates.find(c => c.id === interview.candidateId);
              return (
                <div key={interview.id} className="p-3 bg-slate-800/50 rounded-lg border-l-2 border-blue-500">
                  <p className="text-xs text-blue-400 font-medium mb-1">{interview.time}</p>
                  <p className="text-sm text-slate-200 font-medium">{interview.type}</p>
                  <p className="text-xs text-slate-400 mt-1">with {candidate?.name || 'Unknown'}</p>
                  <div className="mt-2 pt-2 border-t border-slate-700 flex justify-between items-center">
                    <span className="text-[10px] text-slate-500">{interview.interviewer}</span>
                    <button className="text-xs text-blue-400 hover:text-blue-300">Join Meet</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardView;